<template>
    <div class="lockstep-landing mb-5">
        <div class="row">
            <div class="col-lg-6 col-12">
                <div class="row">
                    <div class="col-xl-6 col-12 mb-3" v-if="hubspot.specialist !== null">
                        <!-- this is shown based on backend condition-->
                        <contact :contact="hubspot.specialist" type="specialist" class="h-100" />
                    </div>
                    <div class="col-xl-6 col-12 mb-3" v-if="hubspot.manager !== null">
                        <contact :contact="hubspot.manager" type="expert" class="h-100" />
                    </div>
                    <div class="col-xl-6 col-12 mb-3">
                        <div class="card mx-1 h-100">
                            <div class="card-body d-flex flex-column">
                                <img src="/img/icons/avid-icons/lockstep_tutorials.svg"
                                    class="lockstep-card-icon" alt=""></img>
                                <h3>Guides & Tutorials</h3>
                                <p class="flex-grow-1">Step-by-step walkthroughs to help you make the most of Avid.</p>
                            </div>
                            <div class="card-footer text-end">
                                <Button type="primary" class="lockstep-button" href="https://help.avidai.com/hc/en-us" target="_blank">Visit
                                    Knowledge Base</Button>
                            </div>
                        </div>
                    </div>
                    <div class="col-xl-6 col-12 mb-3">
                        <div class="card mx-1 h-100">
                            <div class="card-body d-flex flex-column">
                                <img src="/img/icons/avid-icons/lockstep_questions.svg"
                                    class="lockstep-card-icon" alt=""></img>
                                <h3>Common Questions</h3>
                                <p class="flex-grow-1">Find quick answers to the most common questions about using Avid.
                                </p>
                            </div>
                            <div class="card-footer text-end">
                                <Button type="primary" class="lockstep-button"
                                    href="https://help.avidai.com/hc/en-us/categories/42778693593491-FAQs-and-Definitions"
                                    target="_blank">View FAQ's</Button>
                            </div>
                        </div>
                    </div>
                    <div class="col-xl-6 col-12 mb-3">
                        <div class="card mx-1 h-100">
                            <div class="card-body d-flex flex-column">
                                <img src="/img/icons/avid-icons/lockstep_system_status.svg"
                                    class="lockstep-card-icon" alt=""></img>
                                <h3>System Status</h3>
                                <p class="flex-grow-1">Check if everything is running smoothly with LockStep and Avid
                                    services.</p>

                                <!-- BetterStack Status Display -->
                                <div v-if="betterstackStatus" class="mt-3">
                                    <!-- Operational Status -->
                                    <div v-if="betterstackStatus.overall_status === 'up' && !betterstackStatus.error"
                                        class="system-status-button status-operational">
                                        <img src="/img/icons/avid-icons/check-circle.svg" class="status-icon" alt="">
                                        <span>Fully Operational</span>
                                    </div>

                                    <!-- Degraded Status (Secondary Systems) with Tooltip -->
                                    <div v-else-if="betterstackStatus.overall_status === 'degraded' && !betterstackStatus.error"
                                        class="system-status-button status-warning"
                                        @mouseenter="showTooltip = true"
                                        @mouseleave="showTooltip = false">
                                        <img src="/img/icons/avid-icons/alert-circle.svg" class="status-icon" alt="">
                                        <span>Some Services are Degraded</span>
                                        
                                        <!-- Tooltip with monitor details -->
                                        <div v-if="showTooltip && activeMonitors.length > 0"
                                            class="status-tooltip">
                                            <div v-for="monitor in activeMonitors" :key="monitor.id"
                                                class="tooltip-monitor-item">
                                                <span class="tooltip-status-dot"
                                                    :class="monitor.is_up ? 'dot-success' : 'dot-danger'"></span>
                                                <span v-if="monitor.is_up">{{ monitor.name }}</span>
                                                <a v-else :href="monitor.url" target="_blank"
                                                    class="tooltip-link">
                                                    {{ monitor.name }}
                                                    <i class="fas fa-external-link-alt ms-1"></i>
                                                </a>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Unavailable Status (Critical Systems) with Tooltip -->
                                    <div v-else-if="betterstackStatus.overall_status === 'down' && !betterstackStatus.error"
                                        class="system-status-button status-unavailable"
                                        @mouseenter="showTooltip = true"
                                        @mouseleave="showTooltip = false">
                                        <img src="/img/icons/avid-icons/x-circle.svg" class="status-icon" alt="">
                                        <span>Temporarily Unavailable</span>
                                        
                                        <!-- Tooltip with monitor details -->
                                        <div v-if="showTooltip && activeMonitors.length > 0"
                                            class="status-tooltip">
                                            <div v-for="monitor in activeMonitors" :key="monitor.id"
                                                class="tooltip-monitor-item">
                                                <span class="tooltip-status-dot"
                                                    :class="monitor.is_up ? 'dot-success' : 'dot-danger'"></span>
                                                <span v-if="monitor.is_up">{{ monitor.name }}</span>
                                                <a v-else :href="monitor.url" target="_blank"
                                                    class="tooltip-link">
                                                    {{ monitor.name }}
                                                    <i class="fas fa-external-link-alt ms-1"></i>
                                                </a>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Error Status -->
                                    <div v-else-if="betterstackStatus.error"
                                        class="system-status-button status-warning">
                                        <img src="/img/icons/avid-icons/alert-circle.svg" class="status-icon" alt="">
                                        <span>Running with Minor Issues</span>
                                    </div>
                                </div>

                                <!-- Loading Status - Default to Fully Operational -->
                                <div v-else class="mt-3">
                                    <div class="system-status-button status-operational">
                                        <img src="/img/icons/avid-icons/check-circle.svg" class="status-icon" alt="">
                                        <span>Fully Operational</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div v-if="is_onboarding" class="col-12 mb-3">
                        <documents :client="client" />
                    </div>
                </div>
            </div>

            <div class="col-lg-6 col-12">
                <div v-if="is_onboarding && lockstepStore.tasks.length > 0" class="mb-3">
                    <tasks />
                </div>
                <div class="mb-3">
                    <tickets />
                </div>

                <div v-if="!is_onboarding" class="mb-3">
                    <documents :client="client" />
                </div>

            </div>
        </div>
    </div>
</template>

<script>
import { defineComponent } from 'vue'
import permissionsMixin from '../../mixins/permissions'
import { useLockstepStore } from '../../stores/lockstep'
import Contact from './Contact.vue'
import Tasks from './Tasks.vue'
import Tickets from './Tickets.vue'
import Documents from './Documents.vue'

export default defineComponent({
    components: {
        Contact,
        Tasks,
        Tickets,
        Documents
    },
    mixins: [permissionsMixin],
    name: 'Lockstep',
    props: {
        client: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            lockstepStore: useLockstepStore(),
            betterstackStatus: null,
            showTooltip: false
        };
    },
    beforeMount() {
        if (!this.lockstepStore.isLoaded && window.__LOCKSTEP__) {
            this.lockstepStore.initialize(window.__LOCKSTEP__);
        }
    },
    mounted() {
        this.lockstepStore.subscribeToEcho(window.Echo);
        this.fetchBetterStackStatus();
    },
    beforeUnmount() {
        this.lockstepStore.unsubscribeFromEcho(window.Echo);
    },
    methods: {
        async fetchBetterStackStatus() {
            try {
                const response = await axios.get('/api/betterstack/status');
                this.betterstackStatus = response.data;
            } catch (error) {
                console.error('Failed to fetch BetterStack status:', error);
                this.betterstackStatus = {
                    error: 'Unable to load status information'
                };
            }
        }
    },
    computed: {
        hubspot() {
            return this.lockstepStore.hubspot;
        },
        is_onboarding() {
            return this.lockstepStore.getClient.is_onboarding;
        },
        activeMonitors() {
            if (!this.betterstackStatus || !this.betterstackStatus.monitors) {
                return [];
            }
            // Only show active monitors (exclude paused/maintenance)
            return this.betterstackStatus.monitors.filter(monitor => 
                monitor.status !== 'paused' && monitor.status !== 'maintenance'
            );
        }
    },
    watch: {

    },
           
});
</script>
<!-- 
<style scoped>
.status-iframe {
    display: block;
    margin: 2rem auto; /* center horizontally and keep vertical spacing */
}
</style> -->